<template>
    <div>
        <!-- Report Table -->
        <q-table
            v-if="selectedSecondaryGrouping === 'assets'"
            :rows="report.rows"
            :columns="table.columns"
            :pagination="table.pagination"
            :no-data-label="$t('No downtimes were found for this date range.')"
            :hide-bottom="report.rows.length > 0"
            :table-class="report.rows.length > 0 ? 'sticky-table' : ''"
            row-key="id"
            :sort-method="rebuildReport"
            :title="label"
            title-class="text-weight-medium text-medium-grey text-body2"
            class="no-shadow border-grey virtual-scroll-table"
            virtual-scroll
            :virtual-scroll-item-size="20"
            :rows-per-page-options="[0]"
        >
            <!-- Header slot -->
            <template v-slot:header="props">
                <q-tr :props="props">
                    <q-th auto-width class="bg-secondary-white" />
                    <q-th
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                        class="bg-secondary-white"
                    >
                        <span
                            class="text-weight-bold"
                            style="font-size: 14px"
                            >{{ col.label }}</span
                        >
                    </q-th>
                </q-tr>
            </template>

            <!-- Row slot -->
            <template v-slot:body="props">
                <q-tr :props="props">
                    <!-- Expansion button -->
                    <q-td auto-width>
                        <q-btn
                            @click="props.expand = !props.expand"
                            :icon="
                                props.expand
                                    ? 'fas fa-caret-up'
                                    : 'fas fa-caret-down'
                            "
                            color="accent"
                            size="sm"
                            round
                            dense
                            ><q-tooltip>{{
                                props.expand
                                    ? $t('Collapse row')
                                    : $t('Expand row')
                            }}</q-tooltip></q-btn
                        >
                    </q-td>
                    <!-- Fields -->
                    <q-td
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                    >
                        <div v-if="isNaN(col.value)">
                            {{ translateLabel(col.value) }}
                            <q-icon
                                v-if="showUnjustifiedDowntimeTooltip(col.value)"
                                name="fa-regular fa-circle-question"
                                color="grey"
                                size="16px"
                                class="q-ml-sm"
                            >
                                <q-tooltip>{{
                                    getUnjustifiedDowntimeTooltipContent(
                                        col.value
                                    )
                                }}</q-tooltip>
                            </q-icon>
                        </div>
                        <div v-else-if="col.name === 'nb_downtime'">
                            {{ !isSpeedLoss(props.row) ? col.value : '-' }}
                        </div>
                        <div v-else-if="col.name === 'downtime_duration'">
                            {{
                                formatProductionValue(
                                    col.value / 60,
                                    userLocale
                                )
                            }}
                        </div>
                        <div v-else-if="col.name === 'accumulated_downtime'">
                            {{ formatPercentage(col.value, userLocale) }}
                        </div>
                    </q-td>
                    <!-- <q-td></q-td> -->
                </q-tr>

                <!-- Expanded row -->
                <template v-for="(row, index) in props.row.assets">
                    <q-tr
                        v-if="props.expand"
                        :key="`${row.assetId}-${row.id}-${index}`"
                    >
                        <q-td auto-width>
                            <div class="q-ml-md">
                                <q-btn
                                    v-if="!isSpeedLoss(row)"
                                    @click="
                                        $emit('handleShowEvents', {
                                            row,
                                            dateInterval: report.dateInterval
                                        })
                                    "
                                    icon="far fa-eye"
                                    color="accent"
                                    size="sm"
                                    round
                                    dense
                                >
                                    <q-tooltip>{{
                                        $t('View events')
                                    }}</q-tooltip>
                                </q-btn>
                            </div>
                        </q-td>
                        <q-td>
                            <div v-if="getAsset(row['assetId'])">
                                {{ getAsset(row['assetId']).name }}
                                <q-badge
                                    v-if="isAssetDeleted(row['assetId'])"
                                    color="lighter-grey"
                                    text-color="medium-grey"
                                    class="q-ml-sm"
                                >
                                    {{ $t('Deleted') }}
                                </q-badge>
                            </div>
                        </q-td>
                        <q-td>
                            <div class="float-right">
                                {{ !isSpeedLoss(row) ? row['nbEvents'] : '-' }}
                            </div>
                        </q-td>
                        <q-td>
                            <div class="float-right">
                                {{
                                    formatProductionValue(
                                        row['totalDuration'] / 60,
                                        userLocale
                                    )
                                }}
                            </div>
                        </q-td>
                        <q-td>
                            <div class="float-right">
                                {{
                                    formatPercentage(
                                        row['cumulativeDowntime'],
                                        userLocale
                                    )
                                }}
                            </div>
                        </q-td>
                    </q-tr>
                    <!-- Expanded row -->
                </template>
            </template>

            <!-- Footer slot -->
            <template v-slot:bottom-row>
                <q-tr
                    v-show="report.rows.length > 0"
                    class="text-bold bg-secondary-white grand-total-row"
                >
                    <q-td auto-width></q-td>
                    <q-td>{{ $t('Grand Total') }}</q-td>
                    <q-td align="right">
                        {{ report.totals.nbEvents }}
                    </q-td>
                    <q-td align="right">
                        {{
                            formatProductionValue(
                                report.totals.totalDuration / 60,
                                userLocale
                            )
                        }}
                    </q-td>
                    <q-td><!-- Cumulative downtime total, keep blank --></q-td>
                </q-tr>
            </template>
        </q-table>

        <q-table
            v-if="selectedSecondaryGrouping === 'products'"
            :rows="report.rows"
            :columns="table.columns"
            :pagination="table.pagination"
            :no-data-label="$t('No downtimes were found for this date range.')"
            :hide-bottom="report.rows.length > 0"
            :table-class="report.rows.length > 0 ? ' sticky-table' : ''"
            row-key="id"
            :sort-method="rebuildReport"
            class="no-shadow border-grey"
        >
            <!-- Header slot -->
            <template v-slot:header="props">
                <q-tr :props="props">
                    <q-th auto-width class="bg-secondary-white" />
                    <q-th
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                        class="bg-secondary-white"
                    >
                        <span
                            class="text-weight-bold"
                            style="font-size: 14px"
                            >{{ col.label }}</span
                        >
                    </q-th>
                </q-tr>
            </template>

            <!-- Row slot -->
            <template v-slot:body="props">
                <q-tr :props="props">
                    <!-- Expansion button -->
                    <q-td auto-width>
                        <q-btn
                            @click="props.expand = !props.expand"
                            :icon="
                                props.expand
                                    ? 'fas fa-caret-up'
                                    : 'fas fa-caret-down'
                            "
                            color="accent"
                            size="sm"
                            round
                            dense
                            ><q-tooltip>{{
                                props.expand
                                    ? $t('Collapse row')
                                    : $t('Expand row')
                            }}</q-tooltip></q-btn
                        >
                    </q-td>
                    <!-- Fields -->
                    <q-td
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                    >
                        <div v-if="isNaN(col.value)">
                            {{ translateLabel(col.value) }}
                        </div>
                        <div v-else-if="col.name === 'nb_downtime'">
                            {{ !isSpeedLoss(props.row) ? col.value : '-' }}
                        </div>
                        <div v-else-if="col.name === 'downtime_duration'">
                            {{
                                formatProductionValue(
                                    col.value / 60,
                                    userLocale
                                )
                            }}
                        </div>
                        <div v-else-if="col.name === 'accumulated_downtime'">
                            {{ formatPercentage(col.value, userLocale) }}
                        </div>
                    </q-td>
                    <!-- <q-td></q-td> -->
                </q-tr>

                <!-- Expanded row -->
                <template v-for="(row, index) in props.row.products">
                    <q-tr
                        v-if="props.expand"
                        :key="`${row.productId}-${row.id}-${index}`"
                    >
                        <q-td auto-width>
                            <div class="q-ml-md">
                                <q-btn
                                    v-if="!isSpeedLoss(row)"
                                    @click="
                                        $emit('handleShowEvents', {
                                            row,
                                            dateInterval: report.dateInterval
                                        })
                                    "
                                    icon="far fa-eye"
                                    color="secondary"
                                    size="sm"
                                    round
                                    dense
                                >
                                    <q-tooltip>{{
                                        $t('View events')
                                    }}</q-tooltip>
                                </q-btn>
                            </div>
                        </q-td>
                        <q-td>
                            <div v-if="row['productName']">
                                {{ row['productName'] }}
                            </div>
                            <div v-else>
                                {{ $t('No Product') }}
                            </div>
                        </q-td>
                        <q-td>
                            <div class="float-right">
                                {{ !isSpeedLoss(row) ? row['nbEvents'] : '-' }}
                            </div>
                        </q-td>
                        <q-td>
                            <div class="float-right">
                                {{
                                    formatProductionValue(
                                        row['totalDuration'] / 60,
                                        userLocale
                                    )
                                }}
                            </div>
                        </q-td>
                        <q-td>
                            <div class="float-right">
                                {{
                                    formatPercentage(
                                        row['cumulativeDowntime'],
                                        userLocale
                                    )
                                }}
                            </div>
                        </q-td>
                    </q-tr>
                    <!-- Expanded row -->
                </template>
            </template>

            <!-- Footer slot -->
            <template v-slot:bottom-row>
                <q-tr
                    v-show="report.rows.length > 0"
                    class="text-bold bg-secondary-white"
                >
                    <q-td auto-width></q-td>
                    <q-td>{{ $t('Grand Total') }}</q-td>
                    <q-td align="right">
                        {{ report.totals.nbEvents }}
                    </q-td>
                    <q-td align="right">
                        {{
                            formatProductionValue(
                                report.totals.totalDuration / 60,
                                userLocale
                            )
                        }}
                    </q-td>
                    <q-td><!-- Cumulative downtime total, keep blank --></q-td>
                </q-tr>
            </template>
        </q-table>
    </div>
</template>

<script>
import {
    formatPercentage,
    formatProductionValue
} from '@utils/numberFormatters.js';
import { mapState } from 'pinia';
import { useAssetsStore } from '@stores/assets.ts';
import { useAuthStore } from '@stores/auth';
import { SPEED_LOSS_CAUSE_ID } from './lossReport';

// Constants for unjustified downtime labels and tooltips
const UNJUSTIFIED_DOWNTIME_LABELS = {
    SPEED_LOSS: 'Speed Losses',
    MAJOR_AVAILABILITY: 'Other (major) - Availability',
    MAJOR_PERFORMANCE: 'Other (major) - Performance',
    MINOR: 'Other (minor)'
};

const TOOLTIP_CONFIG = {
    [UNJUSTIFIED_DOWNTIME_LABELS.SPEED_LOSS]:
        'Speed losses due to assets operating at a run rate lower than expected.',
    [UNJUSTIFIED_DOWNTIME_LABELS.MAJOR_AVAILABILITY]:
        'All unjustified major events, improve data entry score to reduce this cause.',
    [UNJUSTIFIED_DOWNTIME_LABELS.MAJOR_PERFORMANCE]:
        'All unjustified major events, improve data entry score to reduce this cause.',
    [UNJUSTIFIED_DOWNTIME_LABELS.MINOR]:
        'All unjustified minor events, reduce the length of the Downtime delay notification to reduce this field.'
};

const TRANSLATABLE_LABELS = [
    UNJUSTIFIED_DOWNTIME_LABELS.SPEED_LOSS,
    UNJUSTIFIED_DOWNTIME_LABELS.MAJOR_AVAILABILITY,
    UNJUSTIFIED_DOWNTIME_LABELS.MAJOR_PERFORMANCE,
    UNJUSTIFIED_DOWNTIME_LABELS.MINOR,
    'Uncategorized (Major) - Availability',
    'Uncategorized (Major) - Performance',
    'Uncategorized (Minor)'
];

export default {
    name: 'LossReportTable',
    components: {},
    emits: ['setOrder', 'handleShowEvents'],
    props: {
        report: {
            type: Object,
            required: true
        },
        selectedSecondaryGrouping: {
            type: String,
            required: true
        },
        selectedGroupBy: {
            type: String,
            required: true
        },
        sortable: {
            type: Boolean,
            default: true
        },
        label: {
            type: String,
            required: false
        }
    },
    computed: {
        ...mapState(useAssetsStore, ['getAsset']),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        table() {
            return {
                columns: [
                    {
                        name: 'name',
                        field: 'name',
                        label: this.$t(
                            this.selectedGroupBy[0].toUpperCase() +
                                this.selectedGroupBy.slice(1)
                        ),
                        align: 'left',
                        format: (val, row) => {
                            if (val) {
                                return val;
                            } else {
                                if (row.categoryId === -1) {
                                    return this.$t('Uncategorized');
                                }
                            }
                        }
                    },
                    {
                        name: 'nb_downtime',
                        field: 'nbEvents',
                        label: this.$t('No. of downtimes'),
                        align: 'right',
                        sortable: this.sortable
                    },
                    {
                        name: 'downtime_duration',
                        field: 'totalDuration',
                        label: this.$t('Downtime (minutes)'),
                        align: 'right',
                        sortable: this.sortable
                    },
                    {
                        name: 'accumulated_downtime',
                        field: 'accumulated_downtime',
                        label: this.$t('Accumulated downtime'),
                        align: 'right',
                        format: (val, row) => {
                            return row.cumulativeDowntime;
                        }
                    }
                ],
                pagination: {
                    sortBy: 'downtime_duration',
                    descending: true,
                    rowsPerPage: 0 // infinite
                }
            };
        }
    },
    methods: {
        formatPercentage,
        formatProductionValue,
        /**
         * Translate label to current locale (language)
         * @param {String} value - label to translate
         * @return {String} translated label or original value
         */
        translateLabel(value) {
            if (TRANSLATABLE_LABELS.includes(value)) {
                return this.$t(value);
            }
            return value;
        },
        /**
         * Rebuilds the table given a new sort parameter as accumulated
         * downtime needs to be recalculated again
         * @param {Object} rows
         * @param {String} sortBy
         * @param {Boolean} descending
         * @return {Object}
         */
        rebuildReport(rows, sortBy, descending) {
            let orderBy = '';
            for (const col of this.table.columns) {
                if (col.name === sortBy) {
                    orderBy = col.field;
                }
            }

            this.$emit('setOrder', {
                orderBy,
                descending
            });

            return rows;
        },
        /**
         * Verify if the row corresponds to a speed loss
         *
         * @param {Object} row
         * @return {Boolean} true if the row pertains to a speed loss
         */
        isSpeedLoss(row) {
            return row['id'] == SPEED_LOSS_CAUSE_ID;
        },
        /**
         * Verify if the asset is deleted
         * @return {Boolean} true if the asset is deleted
         */
        isAssetDeleted(assetId) {
            const asset = this.getAsset(assetId);
            if (asset && asset.is_deleted) {
                return asset.is_deleted;
            }
            return false;
        },
        /**
         * Check if tooltip should be shown for unjustified downtime label
         * @param {String} value - label to check
         * @return {Boolean} true if the tooltip should be shown
         */
        showUnjustifiedDowntimeTooltip(value) {
            return value in TOOLTIP_CONFIG;
        },
        /**
         * Get tooltip content for unjustified downtime label
         * @param {String} value - label to get tooltip for
         * @return {String} translated tooltip content or empty string
         */
        getUnjustifiedDowntimeTooltipContent(value) {
            const tooltipKey = TOOLTIP_CONFIG[value];
            return tooltipKey ? this.$t(tooltipKey) : '';
        }
    }
};
</script>

<style lang="scss" scoped>
:deep(.q-table__top) {
    background-color: $secondary-white;
}

.virtual-scroll-table {
    max-height: 600px; // Fixed height required for virtual scroll

    // Make the grand total row sticky to the bottom
    :deep(.grand-total-row) {
        position: sticky;
        bottom: 0;
        z-index: 1;
    }
}
</style>
